import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { logger } from '../utils/logger';

/**
 * PRD UI state interface.
 *
 * Manages UI state for PRD generation conversation:
 * - Panel open/closed state
 * - Multiple active sessions (one per project)
 * - Last updated timestamp per project
 *
 * Note: Server state (PRD content, questions, completeness) is managed by TanStack Query.
 */
interface PRDState {
  // UI state
  isPanelExpanded: boolean;
  sessionsByProject: Record<string, string>; // projectId -> sessionId
  lastUpdatedByProject: Record<string, Date | null>; // projectId -> lastUpdated

  // Actions
  openPanel: (projectId: string) => void;
  closePanel: () => void;
  setActiveSession: (projectId: string, sessionId: string) => void;
  clearSession: (projectId?: string) => void;
  getSessionForProject: (projectId: string) => string | null;
  cleanupExpiredSessions: () => void;
}

/**
 * Zustand store for PRD UI state management with localStorage persistence.
 *
 * Supports multiple concurrent PRD sessions (one per project). Persists sessions
 * with 30-minute TTL matching backend expiry. UI state (isPanelExpanded) is not
 * persisted and resets on reload.
 *
 * @example
 * ```tsx
 * // In component
 * const { openPanel } = usePRDStore();
 * const sessionId = useSessionForProject(projectId);
 * ```
 */
export const usePRDStore = create<PRDState>()(
  persist(
    (set, get) => ({
      // Initial state
      isPanelExpanded: false,
      sessionsByProject: {},
      lastUpdatedByProject: {},

      // Actions
      openPanel: (_projectId: string): void => {
        set({
          isPanelExpanded: true,
        });
      },

      closePanel: (): void => {
        set({
          isPanelExpanded: false,
          // Note: Session state is preserved to allow resume
        });
      },

      setActiveSession: (projectId: string, sessionId: string): void => {
        set(state => ({
          sessionsByProject: { ...state.sessionsByProject, [projectId]: sessionId },
          lastUpdatedByProject: { ...state.lastUpdatedByProject, [projectId]: new Date() },
        }));
      },

      clearSession: (projectId?: string): void => {
        if (projectId) {
          // Clear session for specific project
          set(state => {
            const remainingSessions = Object.fromEntries(
              Object.entries(state.sessionsByProject).filter(([key]) => key !== projectId),
            );
            const remainingUpdated = Object.fromEntries(
              Object.entries(state.lastUpdatedByProject).filter(([key]) => key !== projectId),
            );
            return {
              sessionsByProject: remainingSessions,
              lastUpdatedByProject: remainingUpdated,
            };
          });
        } else {
          // Clear all sessions
          set({
            sessionsByProject: {},
            lastUpdatedByProject: {},
            // Note: isPanelExpanded is NOT cleared (user may want to start new session)
          });
        }
      },

      getSessionForProject: (projectId: string): string | null => {
        const { sessionsByProject } = get();
        return sessionsByProject[projectId] || null;
      },

      cleanupExpiredSessions: (): void => {
        const SESSION_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes (match backend TTL)
        set(state => {
          const now = Date.now();
          const cleanedSessions: Record<string, string> = {};
          const cleanedUpdated: Record<string, Date | null> = {};
          const removedSessions: Array<{
            ageMinutes: number;
            projectId: string;
            sessionId: string;
          }> = [];

          Object.entries(state.sessionsByProject).forEach(([projectId, sessionId]) => {
            const lastUpdated = state.lastUpdatedByProject[projectId];
            if (lastUpdated) {
              const age = now - lastUpdated.getTime();
              if (age < SESSION_EXPIRY_MS) {
                // Keep non-expired session
                cleanedSessions[projectId] = sessionId;
                cleanedUpdated[projectId] = lastUpdated;
              } else {
                // Track removed session for logging
                removedSessions.push({
                  projectId,
                  sessionId,
                  ageMinutes: Math.round(age / 60000),
                });
              }
            }
          });

          // Log cleanup activity
          if (removedSessions.length > 0) {
            logger.info(
              { component: 'usePRDStore' },
              'cleanupExpiredSessions: removed expired sessions',
              { removedSessions, remainingCount: Object.keys(cleanedSessions).length },
            );
          }

          return {
            sessionsByProject: cleanedSessions,
            lastUpdatedByProject: cleanedUpdated,
          };
        });
      },
    }),
    {
      name: 'prd-session-state',
      // Only persist critical state needed for session resume
      partialize: state => ({
        sessionsByProject: state.sessionsByProject,
        lastUpdatedByProject: Object.fromEntries(
          Object.entries(state.lastUpdatedByProject).map(([key, value]) => [
            key,
            value ? value.toISOString() : null,
          ]),
        ),
      }),
      // Custom storage adapter to handle Date serialization/deserialization
      storage: {
        getItem: name => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            const parsed = JSON.parse(str);
            // Convert lastUpdatedByProject ISO strings back to Date objects
            if (parsed?.state?.lastUpdatedByProject) {
              parsed.state.lastUpdatedByProject = Object.fromEntries(
                Object.entries(parsed.state.lastUpdatedByProject).map(([key, value]) => {
                  if (value && typeof value === 'string') {
                    const date = new Date(value);
                    // Validate date is valid (not Invalid Date)
                    if (Number.isNaN(date.getTime())) {
                      logger.warn(
                        { component: 'usePRDStore' },
                        'Invalid date in stored state, clearing for project',
                        { projectId: key, lastUpdated: value },
                      );
                      return [key, null];
                    }
                    return [key, date];
                  }
                  return [key, value];
                }),
              );
            }
            return parsed;
          } catch (error) {
            logger.error(
              { component: 'usePRDStore' },
              'Failed to parse stored PRD state',
              error instanceof Error ? error : undefined,
              { error: String(error) },
            );
            // Clear corrupted data to prevent persistent errors
            try {
              localStorage.removeItem(name);
            } catch (removeError) {
              logger.error(
                { component: 'usePRDStore' },
                'Failed to remove corrupted state',
                removeError instanceof Error ? removeError : undefined,
                { error: String(removeError) },
              );
            }
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            // Validate that Date objects are properly serialized before storing
            const serialized = JSON.stringify(value);
            localStorage.setItem(name, serialized);
          } catch (error) {
            // Handle quota exceeded or other storage errors
            if (error instanceof Error && error.name === 'QuotaExceededError') {
              logger.error(
                { component: 'usePRDStore' },
                'localStorage quota exceeded, clearing old data',
              );
              // Try to clear and retry once
              try {
                localStorage.removeItem(name);
                localStorage.setItem(name, JSON.stringify(value));
              } catch (retryError) {
                logger.error(
                  { component: 'usePRDStore' },
                  'Failed to save PRD state after cleanup',
                  retryError instanceof Error ? retryError : undefined,
                  { error: String(retryError) },
                );
              }
            } else {
              logger.error(
                { component: 'usePRDStore' },
                'Failed to save PRD state to localStorage',
                error instanceof Error ? error : undefined,
                { error: String(error) },
              );
            }
          }
        },
        removeItem: name => {
          try {
            localStorage.removeItem(name);
          } catch (error) {
            logger.error(
              { component: 'usePRDStore' },
              'Failed to remove item from localStorage',
              error instanceof Error ? error : undefined,
              { error: String(error) },
            );
          }
        },
      },
      // Hydration validation callback - only logs errors
      // Note: cleanupExpiredSessions is called via onFinishHydration after store creation
      onRehydrateStorage:
        (): ((state: PRDState | undefined, error: unknown) => void) => (_state, error) => {
          if (error) {
            logger.error(
              { component: 'usePRDStore' },
              'Failed to rehydrate PRD store',
              error instanceof Error ? error : undefined,
              { error: String(error) },
            );
          }
        },
    },
  ),
);

// Register hydration callback after store is created (avoids circular reference)
usePRDStore.persist.onFinishHydration(() => {
  usePRDStore.getState().cleanupExpiredSessions();
});

/**
 * Optimized selector hook for active session ID for a specific project.
 *
 * Component only re-renders when the session for this project changes.
 *
 * @param projectId - The project ID to get the session for
 * @returns The session ID for the project, or null if none exists
 *
 * @example
 * ```tsx
 * const sessionId = useActiveSessionId(projectId);
 * const { data } = useQuery({
 *   queryKey: ['prd-preview', sessionId],
 *   enabled: !!sessionId,
 * });
 * ```
 */
export const useActiveSessionId = (projectId: string | null): string | null =>
  usePRDStore(state => (projectId ? state.sessionsByProject[projectId] || null : null));

/**
 * Optimized selector hook for session ID for a specific project.
 *
 * Alias for useActiveSessionId with clearer naming for multi-project context.
 *
 * @param projectId - The project ID to get the session for
 * @returns The session ID for the project, or null if none exists
 *
 * @example
 * ```tsx
 * const sessionId = useSessionForProject(projectId);
 * ```
 */
export const useSessionForProject = (projectId: string | null): string | null =>
  usePRDStore(state => (projectId ? state.sessionsByProject[projectId] || null : null));

/**
 * Optimized selector hook for panel expansion state.
 *
 * Component only re-renders when isPanelExpanded changes.
 *
 * @example
 * ```tsx
 * const isExpanded = useIsPanelExpanded();
 * ```
 */
export const useIsPanelExpanded = (): boolean => usePRDStore(state => state.isPanelExpanded);

/**
 * Hook to check if the store has finished hydrating from localStorage.
 * Uses Zustand's built-in persist.hasHydrated() and onFinishHydration APIs.
 *
 * Use this to prevent starting new sessions before checking for existing ones.
 *
 * @example
 * ```tsx
 * const hasHydrated = useHasHydrated();
 * if (!hasHydrated) {
 *   return <LoadingSpinner />;
 * }
 * ```
 */
export const useHasHydrated = (): boolean => {
  const [hydrated, setHydrated] = useState(usePRDStore.persist.hasHydrated());

  useEffect(() => {
    const unsubHydrate = usePRDStore.persist.onHydrate(() => setHydrated(false));
    const unsubFinishHydration = usePRDStore.persist.onFinishHydration(() => setHydrated(true));
    setHydrated(usePRDStore.persist.hasHydrated());
    return (): void => {
      unsubHydrate();
      unsubFinishHydration();
    };
  }, []);

  return hydrated;
};
